import { css } from '@emotion/react';
import { ColDef, GetDataPath, GetRowIdParams, RowClassParams, RowClickedEvent } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { AgTableWithBaseStyle } from '../../../../components/AgTable/AgTable2';
import { agForesightCompactTheme } from '../../../../theme/ag-foresight';
import { buildClassPaths, IEquityLot, IEquityTotals, sortLotsByClass } from './equity-holdings-model';
import {
  DistinctFundsAggFunc,
  distinctFundsAggFunc,
  getSecurityOverviewAutoGroupColDef,
  getSecurityOverviewColDefs,
} from './securityOverviewColDefs';

const gridContainer = css`
  padding: 0 0.5rem 0.5rem;

  /* The prototype makes the whole row the hit target for expanding, not just the chevron
     (tr.expandable{cursor:pointer}), so expandable rows advertise it. */
  .ag-row.equity-row-expandable {
    cursor: pointer;
  }
`;

// Compact, matching Security History and the sibling holdings grid on this page, with the same
// halved group indent — the width belongs to the security names.
const overviewGridTheme = agForesightCompactTheme.withParams({
  fontSize: 12,
  rowGroupIndentSize: '14px',
});

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  flex: 1,
};

const aggFuncs = { [DistinctFundsAggFunc]: distinctFundsAggFunc };

interface ISecurityOverviewGridProps {
  /** Already narrowed to what is still on the books — see `selectHeldLots`. */
  heldLots: IEquityLot[];
  /** Totals of exactly `heldLots`, passed in rather than recomputed so the pinned Total row cannot
   *  drift from the KPI strip above it. */
  totals: IEquityTotals;
}

/**
 * Security Overview: what is currently held, as a class → buying block → fund tree. Realized,
 * converted and written-off positions are Security History's job.
 */
export function SecurityOverviewGrid({ heldLots, totals }: ISecurityOverviewGridProps) {
  // Sorted here because AG Grid builds its tree groups in row order.
  const rowData = useMemo(() => sortLotsByClass(heldLots), [heldLots]);
  const paths = useMemo(() => buildClassPaths(heldLots), [heldLots]);

  const columnDefs = useMemo(() => getSecurityOverviewColDefs(), []);
  const autoGroupColumnDef = useMemo(() => getSecurityOverviewAutoGroupColDef(), []);

  const getDataPath = useCallback<GetDataPath<IEquityLot>>(
    (lot) => paths.get(lot.id) ?? [lot.securityClass],
    [paths]
  );

  const getRowId = useCallback((params: GetRowIdParams<IEquityLot>) => params.data.id, []);

  // Clicking anywhere on a group row toggles it, matching the prototype. Leaf rows and the pinned
  // Total have nothing to expand, so they are left alone.
  const onRowClicked = useCallback((event: RowClickedEvent<IEquityLot>) => {
    const { node } = event;
    if (node.rowPinned || !node.group) {
      return;
    }
    node.setExpanded(!node.expanded);
  }, []);

  const getRowClass = useCallback(
    ({ node }: RowClassParams<IEquityLot>) =>
      !node.rowPinned && node.group ? 'equity-row-expandable' : undefined,
    []
  );

  // The design pins Total directly under the header. Shaped like a lot so the same colDefs render it;
  // the cell renderers key off `node.rowPinned` for the label and the empty Fund cell.
  const pinnedTopRowData = useMemo<IEquityLot[]>(
    () => [
      {
        id: 'total',
        restructureId: '',
        fundId: null,
        securityName: 'Total',
        securityType: '',
        securityClass: '',
        initialHolding: null,
        investmentDate: null,
        shares: totals.shares,
        invested: totals.invested,
        fmv: totals.fmv,
        proceeds: totals.proceeds,
        ownershipPct: totals.ownershipPct,
        // The pinned row is a totals stand-in, not a security, so it has no whole-security figure.
        securityOwnershipPct: null,
        moic: totals.moic,
        lifecycle: 'active',
      },
    ],
    [totals]
  );

  return (
    <div css={gridContainer}>
      <AgTableWithBaseStyle
        aggFuncs={aggFuncs}
        autoGroupColumnDef={autoGroupColumnDef}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout='autoHeight'
        getDataPath={getDataPath}
        getRowClass={getRowClass}
        getRowId={getRowId}
        onRowClicked={onRowClicked}
        groupDefaultExpanded={0}
        pinnedTopRowData={pinnedTopRowData}
        rowData={rowData}
        suppressAggFuncInHeader
        theme={overviewGridTheme}
        treeData
      />
    </div>
  );
}
